import { captureError } from '@smg-real-estate/browser-rum';
import type { ActionContext } from 'vuex';

import * as ToplistingApiService from '@/api/toplistingService';
import { ApiCallStatus } from '@/store/insertions/InsertionsStore';
import type { InsertionFunnelState } from '@/store/types/insertion-funnel-store';
import { useAgencyStore } from '@/stores/agency/useAgencyStore';
import type { RegionalHighlightPurchaseOrderRequest } from '@/types/regionalHighlight';

import type {
  RegionalHighlightOperation,
  RegionalHighlightStateFields,
} from './regionalHighlightStoreSlice';

type RegionalHighlightActionContext = ActionContext<
  RegionalHighlightStateFields,
  InsertionFunnelState
>;

const isActiveRegionalHighlightAgency = (capturedAgencyId: string): boolean =>
  useAgencyStore().activeAgencyId === capturedAgencyId;

const commitRegionalHighlightOperationWithoutAgency = (
  context: RegionalHighlightActionContext,
  operation: RegionalHighlightOperation,
): void => {
  context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.ERROR });
};

const requireActiveAgencyId = (
  context: RegionalHighlightActionContext,
  operation: RegionalHighlightOperation,
): string | undefined => {
  const activeAgencyId = useAgencyStore().activeAgencyId;
  if (!activeAgencyId) {
    commitRegionalHighlightOperationWithoutAgency(context, operation);
    return undefined;
  }
  return activeAgencyId;
};

export const runRegionalHighlightOperation = async (
  context: RegionalHighlightActionContext,
  operation: RegionalHighlightOperation,
  capturedAgencyId: string,
  action: () => Promise<(() => void) | undefined>,
): Promise<boolean> => {
  if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
    return false;
  }

  context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.IN_PROGRESS });
  context.commit('clearRegionalHighlightError', operation);

  try {
    const commitResult = await action();
    if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
      return false;
    }
    commitResult?.();
    context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.SUCCESS });
    return true;
  } catch (error) {
    if (!isActiveRegionalHighlightAgency(capturedAgencyId)) {
      return false;
    }
    context.commit('setRegionalHighlightApiStatus', { operation, status: ApiCallStatus.ERROR });
    context.commit('setRegionalHighlightError', { operation, error });
    captureError(error);
    return false;
  }
};

export const regionalHighlightPurchaseActions = {
  async fetchRegionalHighlightPurchaseOptions(
    context: RegionalHighlightActionContext,
  ): Promise<void> {
    const activeAgencyId = requireActiveAgencyId(context, 'purchaseOptions');
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(context, 'purchaseOptions', activeAgencyId, async () => {
      const options =
        await ToplistingApiService.fetchRegionalHighlightPurchaseOptions(activeAgencyId);
      return () => context.commit('setRegionalHighlightPurchaseOptions', options);
    });
  },
  async fetchRegionalHighlightPurchaseOrders(
    context: RegionalHighlightActionContext,
  ): Promise<void> {
    const activeAgencyId = requireActiveAgencyId(context, 'purchaseOrders');
    if (!activeAgencyId) {
      return;
    }

    await runRegionalHighlightOperation(context, 'purchaseOrders', activeAgencyId, async () => {
      const orders =
        await ToplistingApiService.fetchRegionalHighlightPurchaseOrders(activeAgencyId);
      return () => context.commit('setRegionalHighlightPurchaseOrders', orders);
    });
  },
  async createRegionalHighlightPurchaseOrder(
    context: RegionalHighlightActionContext,
    request: RegionalHighlightPurchaseOrderRequest,
  ): Promise<void> {
    const activeAgencyId = requireActiveAgencyId(context, 'createPurchaseOrder');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      context,
      'createPurchaseOrder',
      activeAgencyId,
      async () => {
        const order = await ToplistingApiService.createRegionalHighlightPurchaseOrder(
          activeAgencyId,
          request,
        );
        return () => context.commit('setRegionalHighlightLastPurchaseOrder', order);
      },
    );
    if (succeeded) {
      await context.dispatch('fetchRegionalHighlightPurchaseOrders');
    }
  },
  async extendRegionalHighlightSlot(
    context: RegionalHighlightActionContext,
    payload: {
      slotId: string;
      request: RegionalHighlightPurchaseOrderRequest;
    },
  ): Promise<void> {
    const activeAgencyId = requireActiveAgencyId(context, 'extendSlot');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      context,
      'extendSlot',
      activeAgencyId,
      async () => {
        const order = await ToplistingApiService.extendRegionalHighlightSlot(
          activeAgencyId,
          payload.slotId,
          payload.request,
        );
        return () => context.commit('setRegionalHighlightLastPurchaseOrder', order);
      },
    );
    if (succeeded) {
      await context.dispatch('fetchRegionalHighlightSlots');
      await context.dispatch('fetchRegionalHighlightPurchaseOrders');
    }
  },
  async setRegionalHighlightAutoRenew(
    context: RegionalHighlightActionContext,
    payload: { slotId: string; autoRenew: boolean },
  ): Promise<void> {
    const activeAgencyId = requireActiveAgencyId(context, 'autoRenew');
    if (!activeAgencyId) {
      return;
    }

    const succeeded = await runRegionalHighlightOperation(
      context,
      'autoRenew',
      activeAgencyId,
      async () => {
        await ToplistingApiService.setRegionalHighlightAutoRenew(
          activeAgencyId,
          payload.slotId,
          payload.autoRenew,
        );
        return undefined;
      },
    );
    if (succeeded) {
      await context.dispatch('fetchRegionalHighlightSlots');
    }
  },
};
